import { Injectable, effect, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { OfflineQueueService } from './offline-queue.service';
import { ConnectivityService } from './connectivity.service';
import { WriteMethod, WriteQueueEntry } from './db';
import { getResponseShape } from './response-shapes';
import { Utils } from '@/utils/utils';

export interface ResolvedSync {
    tempId: number;
    realId: number;
    entityType: string;
}

const ENTITY_LABELS: Record<string, string> = {
    documents: 'Documento',
    tickets: 'Ticket',
    purchases: 'Compra',
    clients: 'Cliente',
    products: 'Producto',
    categories: 'Categoría',
    measurements: 'Medida',
    providers: 'Proveedor',
    pay_methods: 'Método de pago',
    roles: 'Rol',
};

@Injectable({
    providedIn: 'root'
})
export class SyncService {

    private http = inject(HttpClient);
    private queue = inject(OfflineQueueService);
    private connectivity = inject(ConnectivityService);

    /** Emits whenever a queued entry syncs and its temp id resolves to a real one. */
    resolved$ = new Subject<ResolvedSync>();

    private replaying = false;

    constructor() {
        window.addEventListener('online', () => this.replay());
        if (navigator.onLine) {
            setTimeout(() => this.replay(), 1000);
        }
        // The `online` event only covers the network interface coming back. When the wifi stayed
        // up but the backend itself was unreachable (see ConnectivityService), this is the only
        // signal that it's answering again.
        effect(() => {
            if (this.connectivity.backendOnline()) this.replay();
        });
    }

    async replay(): Promise<void> {
        if (this.replaying || !navigator.onLine) return;
        this.replaying = true;
        try {
            let entry = await this.queue.claimNextPending();
            while (entry) {
                const reachedBackend = await this.replayEntry(entry);
                if (!reachedBackend || !navigator.onLine) break;
                entry = await this.queue.claimNextPending();
            }
        } finally {
            this.replaying = false;
        }
    }

    /** Returns false when the backend couldn't be reached at all, so replay() stops instead of burning through the rest of the queue. */
    private async replayEntry(entry: WriteQueueEntry): Promise<boolean> {
        try {
            const body = entry.body ? await this.rewriteTempIds(entry.body) : entry.body;
            const url = await this.rewriteUrlTempId(entry.url);

            const response: any = await firstValueFrom(this.sendReal(entry.method, url, body));

            if (entry.tempId != null) {
                const realId = this.extractRealId(entry.entityType, response);
                if (realId != null) {
                    await this.queue.mapId(entry.tempId, realId, entry.entityType);
                    this.resolved$.next({ tempId: entry.tempId, realId, entityType: entry.entityType });
                }
            }

            await this.queue.update({ ...entry, status: 'synced', lastError: null });
            Utils.notification.success(`${this.describeEntry(entry)} sincronizado`);
            return true;
        } catch (err: any) {
            if (err?.status === 0) {
                // Nothing reached the server (wifi up but backend down, or the connection dropped
                // mid-replay): not a rejection, so put it back as pending for the next replay
                // rather than parking it as `failed`, which only a manual retry would pick up.
                await this.queue.update({ ...entry, status: 'pending', lastError: this.describeError(err) });
                return false;
            }
            await this.queue.update({ ...entry, status: 'failed', lastError: this.describeError(err) });
            return true;
        }
    }

    private sendReal(method: WriteMethod, url: string, body: any) {
        switch (method) {
            case 'POST': return this.http.post(url, body);
            case 'PUT': return this.http.put(url, body);
            case 'DELETE': return this.http.delete(url);
        }
    }

    /** Deep-replaces any negative number already resolved via id_map (e.g. a client_id from an earlier queued create) with its real id. */
    private async rewriteTempIds<T>(value: T): Promise<T> {
        const idMap = await this.queue.getAllIdMappings();
        const walk = (v: any): any => {
            if (Array.isArray(v)) return v.map(walk);
            if (v && typeof v === 'object') {
                const out: any = {};
                for (const k of Object.keys(v)) out[k] = walk(v[k]);
                return out;
            }
            if (typeof v === 'number' && v < 0 && idMap.has(v)) return idMap.get(v);
            return v;
        };
        return walk(value);
    }

    /** An update/delete queued against a temp id needs its URL's trailing id swapped for the real one, once known. */
    private async rewriteUrlTempId(url: string): Promise<string> {
        const match = url.match(/\/(-\d+)$/);
        if (!match) return url;
        const tempId = Number(match[1]);
        const realId = await this.queue.getRealIdForTemp(tempId);
        if (realId == null) return url;
        return url.replace(/\/-\d+$/, `/${realId}`);
    }

    private extractRealId(entityType: string, response: any): number | null {
        const shape = getResponseShape(entityType);
        if (shape.family === 'wrapped' && shape.key) {
            return response?.[shape.key]?.id ?? null;
        }
        if (shape.family === 'raw') {
            return response?.id ?? null;
        }
        return null; // message-only family (providers/pay_methods) - no real id to reconcile with
    }

    /**
     * ErrorInterceptor hands back a raw HttpErrorResponse only for status 0/401/403; for any other
     * rejection it unwraps it to `err.error.message || err.error`, i.e. a string or the bare
     * response body (`{ errors: '...' }` etc.) - so read both shapes, not just `err.error.message`.
     */
    private describeError(err: any): string {
        if (err?.status === 0) return 'Sin conexión con el servidor';
        const body = err?.error && typeof err.error === 'object' ? err.error : null;
        return Utils.extractErrorMessage(body ?? err) || 'Error al sincronizar';
    }

    private describeEntry(entry: WriteQueueEntry): string {
        return ENTITY_LABELS[entry.entityType] ?? 'Registro';
    }
}
